import { Flex, Text } from "@chakra-ui/layout";
import { IoIosNotifications } from "react-icons/io";
import { Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/menu";
import { Badge, useToast } from "@chakra-ui/react";
import { useMemo, useCallback, useRef } from "react";
import { useNotificacoes } from "core/features/notificacao/hooks/useNotificacoes";
import moment from "moment";
import {
  NotificacaoPaginateResponse,
  ActionTargetNotificationEnum,
} from "core/features/notificacao/typings/index";
import { Spin, Empty } from "antd";
import { useVisualizarNotificacao } from "core/features/notificacao/hooks/useVisualizarNotificacao";
import colors from "core/resources/theme/colors";
import history from "infra/helpers/history";
import DefaultButton from "ui/components/Button/PrimaryButton";

function NotificationMenu() {
  const {
    data,
    isLoading,
    isRefetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useNotificacoes();

  const toast = useToast();

  const { mutate: handleVisualize } = useVisualizarNotificacao();

  const localVisualized = useRef<Set<string>>(new Set());

  const _handleClick = useCallback(
    (notificacao: NotificacaoPaginateResponse) => {
      handleVisualize(notificacao._id);
      localVisualized.current.add(notificacao._id);
      if (notificacao.actionTarget === ActionTargetNotificationEnum.copy) {
        navigator.clipboard.writeText(notificacao.action);
        toast({
          position: "top-right",
          title: `Valor copiado.`,

          status: "success",
          duration: 5000,
          isClosable: true,
        });
        return;
      } else if (
        notificacao.actionTarget === ActionTargetNotificationEnum.external
      ) {
        return window.open(notificacao.action, "_blank");
      } else {
        history.push(notificacao.action);
      }
    },
    [handleVisualize, toast]
  );

  const items = useMemo(() => {
    return (
      data?.pages.reduce(
        (agg: NotificacaoPaginateResponse[], item) =>
          agg.concat(item.items ?? []),
        []
      ) ?? []
    );
  }, [data]);

  const formatTime = (data: number) => {
    if (moment().diff(moment(data), "day") >= 1) {
      return moment(data).format("DD/MM/YYYY HH:mm");
    }
    return `há ${moment().diff(moment(data), "hours")} horas`;
  };

  const notVisualized = items.reduce(
    (acc, next) => (acc += next.visualizedAt ? 0 : 1),
    0
  );

  return (
    <Menu>
      <MenuButton _hover={{ opacity: 0.8 }}>
        <Flex pr="16px">
          <IoIosNotifications fontSize="32px" color="#535e7a" />
          {notVisualized > 9 ? (
            <Badge colorScheme="red" variant="solid" position="absolute">
              9+
            </Badge>
          ) : notVisualized > 0 ? (
            <Badge colorScheme="red" variant="solid" position="absolute">
              {notVisualized}
            </Badge>
          ) : null}
        </Flex>
      </MenuButton>
      <MenuList style={{ maxWidth: 550 }}>
        <Text fontWeight="bold" color="#015182" pl="10px">
          Notificações
        </Text>

        <Flex
          alignItems="center"
          flexDir="column"
          pr="4px"
          maxH="400px"
          overflowY="auto"
          className="custom-scroll-bar"
        >
          {items.map((notification, index) => (
            <MenuItem key={index} onClick={() => _handleClick(notification)}>
              {/* <Flex pr="8px">
          <Avatar name="Dan Abrahmov" src="https://bit.ly/dan-abramov" />
        </Flex> */}
              <Flex flexDir="column">
                <Flex align="center" gap={1}>
                  <Text fontWeight="bold" fontSize="12px">
                    {notification.title}
                  </Text>
                  {!notification.visualizedAt &&
                  !localVisualized.current.has(notification._id) ? (
                    <Flex rounded="full" bg={colors.orange} w={1} h={1} />
                  ) : null}
                </Flex>
                <Text fontSize="12px">{notification.description || ""}</Text>
                <Text fontWeight="light" fontSize="12px">
                  {formatTime(notification.createdAt)}
                </Text>
              </Flex>
            </MenuItem>
          ))}

          {!isLoading &&
          !isFetchingNextPage &&
          !isRefetching &&
          !items.length ? (
            <Empty description="Sem notificações" />
          ) : null}

          {isLoading || isFetchingNextPage ? (
            <Spin size="small" spinning />
          ) : null}

          {!isLoading && !isRefetching && hasNextPage ? (
            <DefaultButton
              bgColor="transparent"
              color={colors.black}
              fontWeight="medium"
              _hover={{ opacity: 0.6, color: colors.darkGray }}
              onClick={() => fetchNextPage()}
            >
              Mostrar mais
            </DefaultButton>
          ) : null}
        </Flex>
      </MenuList>
    </Menu>
  );
}

export default NotificationMenu;
